// Minimal, safe markdown renderer for our own docs pages (all text is HTML-escaped).

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
export const slug = (s: string) => s.toLowerCase().replace(/<[^>]+>/g, '').replace(/[^a-z0-9 _-]/g, '').trim().replace(/ /g, '-');

const inline = (raw: string): string => {
  const codes: string[] = [];
  let s = raw.replace(/`([^`]+)`/g, (_, c) => { codes.push(c); return `\uE000${codes.length - 1}\uE001`; });
  s = esc(s);
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[^*])\*([^*\s][^*]*)\*/g, '$1<em>$2</em>');
  s = s.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, text, href) => {
    const safe = /^(https?:|#|\/)/.test(href) ? href : '#';
    const ext = /^https?:/.test(safe);
    return `<a href="${safe}"${ext ? ' target="_blank" rel="noopener noreferrer"' : ''}>${text}</a>`;
  });
  s = s.replace(/\\\|/g, '|');
  return s.replace(/\uE000(\d+)\uE001/g, (_, i) => `<code>${esc(codes[Number(i)])}</code>`);
};

const splitRow = (line: string) =>
  line.trim().replace(/^\||\|$/g, '').split(/(?<!\\)\|/).map(c => c.trim());

export const renderMarkdown = (md: string): { html: string; toc: { level: number; text: string; id: string }[] } => {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  const toc: { level: number; text: string; id: string }[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^```/.test(line)) {
      const lang = line.slice(3).trim();
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) buf.push(lines[i++]);
      i++;
      out.push(`<div class="ca-code mb-3"><div class="ca-code-head px-3 py-1 d-flex justify-content-between"><span class="small text-white-50">${esc(lang)}</span><button type="button" class="btn btn-sm btn-link text-white-50 p-0 text-decoration-none ca-copy">Copy</button></div><pre class="m-0 px-3 py-2"><code>${esc(buf.join('\n'))}</code></pre></div>`);
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1].length;
      const id = slug(h[2]);
      if (level >= 2 && level <= 3) toc.push({ level, text: h[2].replace(/`/g, ''), id });
      out.push(`<h${level} id="${id}" class="${level === 1 ? 'fw-bold mb-3' : 'fw-bold mt-4 mb-2'}">${inline(h[2])}</h${level}>`);
      i++;
      continue;
    }
    if (/^---+\s*$/.test(line)) { out.push('<hr/>'); i++; continue; }
    if (/^\|/.test(line) && i + 1 < lines.length && /^\|?\s*:?-{3,}/.test(lines[i + 1])) {
      const head = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && /^\|/.test(lines[i])) rows.push(splitRow(lines[i++]));
      out.push(`<div class="table-responsive mb-3"><table class="table table-sm table-bordered small"><thead class="table-light"><tr>${head.map(c => `<th>${inline(c)}</th>`).join('')}</tr></thead><tbody>${rows.map(r => `<tr>${r.map(c => `<td>${inline(c)}</td>`).join('')}</tr>`).join('')}</tbody></table></div>`);
      continue;
    }
    if (/^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line)) {
      const ordered = /^\s*\d+\.\s+/.test(line);
      const items: string[] = [];
      while (i < lines.length && (ordered ? /^\s*\d+\.\s+/ : /^\s*[-*]\s+/).test(lines[i])) {
        items.push(lines[i].replace(ordered ? /^\s*\d+\.\s+/ : /^\s*[-*]\s+/, ''));
        i++;
      }
      out.push(`<${ordered ? 'ol' : 'ul'} class="mb-3">${items.map(t => `<li>${inline(t)}</li>`).join('')}</${ordered ? 'ol' : 'ul'}>`);
      continue;
    }
    if (!line.trim()) { i++; continue; }
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,4}\s|```|\||\s*[-*]\s|\s*\d+\.\s|---)/.test(lines[i])) para.push(lines[i++]);
    if (!para.length) { para.push(lines[i++]); }
    out.push(`<p>${inline(para.join(' '))}</p>`);
  }
  return { html: out.join('\n'), toc };
};

